"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import type { CreateChartInput } from "@/lib/api";

interface PlaceResult {
  label: string;
  name: string;
  state: string | null;
  country: string | null;
  countryCode: string | null;
  lat: number;
  lng: number;
}

interface Props {
  onSubmit: (data: CreateChartInput) => void;
  isLoading: boolean;
  label?: string;
  submitLabel?: string;
  initialData?: CreateChartInput;
}

export default function ChartForm({ onSubmit, isLoading, label, submitLabel, initialData }: Props) {
  const t = useTranslations("chartForm");

  const [name, setName] = useState(initialData?.name ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(initialData?.dateOfBirth ?? "1900-12-31");
  const [timeOfBirth, setTimeOfBirth] = useState(initialData?.timeOfBirth ?? "12:00");
  const [timeUnknown, setTimeUnknown] = useState(initialData?.timeUnknown ?? false);
  const [placeQuery, setPlaceQuery] = useState(
    initialData ? `${initialData.town ?? ""}${initialData.country ? `, ${initialData.country}` : ""}` : ""
  );
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(
    initialData
      ? {
          label: `${initialData.town ?? ""}${initialData.country ? `, ${initialData.country}` : ""}`,
          name: initialData.town ?? "",
          state: null,
          country: initialData.country ?? null,
          countryCode: null,
          lat: initialData.latitude,
          lng: initialData.longitude,
        }
      : null
  );

  const [suggestions, setSuggestions] = useState<PlaceResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(`/api/places?q=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error("fetch failed");
      const data: PlaceResult[] = await res.json();
      setSuggestions(data);
      setShowDropdown(data.length > 0);
    } catch {
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setSearching(false);
    }
  }, []);

  function handlePlaceInput(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setPlaceQuery(val);
    setSelectedPlace(null); // clear selection when user types

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  }

  function handleSelectPlace(place: PlaceResult) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSelectedPlace(place);
    setPlaceQuery(place.label);
    setSuggestions([]);
    setShowDropdown(false);
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlace) return;
    onSubmit({
      name: name || undefined,
      dateOfBirth,
      timeOfBirth: timeUnknown ? undefined : timeOfBirth || undefined,
      timeUnknown,
      latitude: selectedPlace.lat,
      longitude: selectedPlace.lng,
      town: selectedPlace.name,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-lg">
      {label && (
        <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wide">{label}</p>
      )}
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("name")}
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("namePlaceholder")}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />
      </div>

      {/* Date of birth */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("dateOfBirth")}
        </label>
        <input
          type="date"
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />
      </div>

      {/* Time of birth */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("timeOfBirth")}
        </label>
        <input
          type="time"
          value={timeOfBirth}
          disabled={timeUnknown}
          onChange={(e) => setTimeOfBirth(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:bg-gray-100"
        />
        <label className="flex items-center gap-2 mt-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={timeUnknown}
            onChange={(e) => setTimeUnknown(e.target.checked)}
            className="rounded"
          />
          {t("timeUnknown")}
        </label>
      </div>

      {/* Place of birth — autocomplete */}
      <div ref={dropdownRef} className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("placeOfBirth")}
        </label>
        <div className="relative">
          <input
            type="text"
            value={placeQuery}
            onChange={handlePlaceInput}
            onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
            placeholder={t("placePlaceholder")}
            autoComplete="off"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 pr-8"
          />
          {searching && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
              ...
            </span>
          )}
          {selectedPlace && !searching && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500 text-sm">
              ✓
            </span>
          )}
        </div>

        {showDropdown && suggestions.length > 0 && (
          <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto text-sm">
            {suggestions.map((place, i) => (
              <li
                key={i}
                onMouseDown={() => handleSelectPlace(place)}
                className="px-3 py-2 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-0"
              >
                {place.label}
              </li>
            ))}
          </ul>
        )}

        {selectedPlace && (
          <p className="mt-1 text-xs text-gray-400">
            {selectedPlace.lat.toFixed(4)}, {selectedPlace.lng.toFixed(4)}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={isLoading || !selectedPlace}
        className="bg-indigo-700 hover:bg-indigo-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50"
      >
        {isLoading ? t("submitting") : (submitLabel ?? t("submit"))}
      </button>
    </form>
  );
}
